import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payments, setPayments] = useState([{ payerId: '', amountPaid: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadSession = async () => {
    try {
      const res = await API.get(`/sessions/${id}`);
      setSession(res.data);
    } catch { navigate('/sessions'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSession(); }, [id]);

  const addPaymentRow = () => setPayments([...payments, { payerId: '', amountPaid: '' }]);
  const removePaymentRow = (i) => setPayments(payments.filter((_, idx) => idx !== i));
  const updatePayment = (i, field, value) => {
    const updated = [...payments];
    updated[i][field] = value;
    setPayments(updated);
  };

  const submitPayments = async () => {
    setError('');
    const valid = payments.filter(p => p.payerId && p.amountPaid > 0);
    if (!valid.length) return setError('Add at least one payment');
    setSubmitting(true);
    try {
      await API.post(`/sessions/${id}/payments`, { payments: valid });
      setShowPayModal(false);
      await loadSession();
    } catch (err) {
      setError(err.response?.data?.error || 'Error logging payments');
    } finally { setSubmitting(false); }
  };

  const markSettled = async (settlementId) => {
    try {
      await API.patch(`/sessions/settlements/${settlementId}/settle`);
      await loadSession();
    } catch (err) { console.error(err); }
  };

  const sendWhatsApp = () => {
    const date = new Date(session.session_date).toLocaleDateString('en-PK', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
    const totalPaid = session.payments.reduce((s, p) => s + parseFloat(p.amount_paid), 0);
    const appLink = `https://pedaltoast.pro/sessions/${id}`;

    let msg = `🎾 *Pedal Toast — ${session.location_name}*\n`;
    msg += `📅 ${date}\n\n`;

    if (session.payments.length > 0) {
      msg += `*💳 Paid by:*\n`;
      session.payments.forEach(p => {
        msg += `• ${p.payer_name}: Rs. ${parseFloat(p.amount_paid).toLocaleString()}\n`;
      });
      msg += `• *Total: Rs. ${totalPaid.toLocaleString()}*\n\n`;
    }

    if (session.guests?.length > 0) {
      msg += `*👤 Guests:*\n`;
      session.guests.forEach(g => {
        msg += `• ${g.guest_name} (host: ${g.host_name})\n`;
      });
      msg += `\n`;
    }

    const unsettled = session.settlements.filter(s => !s.settled);
    const settled = session.settlements.filter(s => s.settled);

    if (unsettled.length > 0) {
      msg += `*💸 Pending Payments:*\n`;
      unsettled.forEach(s => {
        msg += `• ${s.from_name} owes ${s.to_name}: Rs. ${parseFloat(s.amount).toLocaleString()}\n`;
      });
      msg += `\n`;
    }

    if (settled.length > 0) {
      msg += `*✅ Already Settled:*\n`;
      settled.forEach(s => {
        msg += `• ${s.from_name} → ${s.to_name}: Rs. ${parseFloat(s.amount).toLocaleString()}\n`;
      });
      msg += `\n`;
    }

    msg += `---\n`;
    msg += `📱 *Open app to mark yourself paid & rate your session:*\n`;
    msg += appLink;

    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!session) return null;

  const totalPaid = session.payments.reduce((s, p) => s + parseFloat(p.amount_paid), 0);
  const shareAmount = session.players.length > 0 ? totalPaid / session.players.length : 0;

  return (
    <div className="app-shell">
      <div className="topbar">
        <button className="btn btn-ghost" onClick={() => navigate('/sessions')} style={{ padding: '8px 0', color: 'var(--text2)' }}>← Back</button>
        <div className="topbar-title" style={{ fontSize: 16 }}>{session.location_name}</div>
        <span className={`badge badge-${session.status}`}>{session.status}</span>
      </div>

      <div className="page fade-in">
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4 }}>
            📅 {new Date(session.session_date).toLocaleDateString('en-PK', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div style={{ color: 'var(--text2)', fontSize: 13 }}>
            👥 {session.players.length} players · Created by {session.created_by_name}
          </div>
          {totalPaid > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div className="row-between">
                <span style={{ color: 'var(--text2)', fontSize: 14 }}>Total Paid</span>
                <span style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: 18 }}>Rs. {totalPaid.toLocaleString()}</span>
              </div>
              <div className="row-between" style={{ marginTop: 6 }}>
                <span style={{ color: 'var(--text2)', fontSize: 14 }}>Share per player</span>
                <span style={{ color: 'var(--accent)', fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: 16 }}>Rs. {shareAmount.toFixed(0)}</span>
              </div>
            </div>
          )}
        </div>

        {session.guests?.length > 0 && (
          <>
            <div className="section-header"><div className="section-title">Guests</div></div>
            {session.guests.map(g => (
              <div key={g.id} className="card" style={{ padding: '14px 18px' }}>
                <div className="flex gap-8">
                  <div className="avatar" style={{ background: '#fef3c7', color: '#d97706' }}>👤</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{g.guest_name} <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 400 }}>(guest)</span></div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Host: {g.host_name} · pays double share</div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        <div className="section-header"><div className="section-title">Players</div></div>
        {session.players.map(p => {
          const paid = session.payments.find(pm => pm.payer_id === p.user_id);
          const amtPaid = paid ? parseFloat(paid.amount_paid) : 0;
          const net = amtPaid - shareAmount;
          return (
            <div key={p.user_id} className="card" style={{ padding: '14px 18px' }}>
              <div className="row-between">
                <div className="flex gap-8">
                  <div className="avatar">{p.name[0].toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.name}{p.user_id === user.id ? ' (you)' : ''}</div>
                    {shareAmount > 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                        Share: Rs. {shareAmount.toFixed(0)}
                        {amtPaid > 0 && ` · Paid: Rs. ${amtPaid.toLocaleString()}`}
                      </div>
                    )}
                  </div>
                </div>
                {shareAmount > 0 && (
                  <span className={`balance-pill ${net > 0.5 ? 'balance-positive' : net < -0.5 ? 'balance-negative' : 'balance-zero'}`} style={{ fontSize: 12 }}>
                    {net > 0.5 ? `+Rs.${net.toFixed(0)}` : net < -0.5 ? `-Rs.${Math.abs(net).toFixed(0)}` : '✓'}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {session.payments.length > 0 && (
          <>
            <div className="section-header" style={{ marginTop: 20 }}><div className="section-title">Who Paid</div></div>
            {session.payments.map(p => (
              <div key={p.id} className="card" style={{ padding: '14px 18px' }}>
                <div className="row-between">
                  <div className="flex gap-8">
                    <div className="avatar" style={{ background: 'var(--success-light)', color: 'var(--success)' }}>💳</div>
                    <div style={{ fontWeight: 600 }}>{p.payer_name}</div>
                  </div>
                  <span style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, color: 'var(--success)' }}>
                    Rs. {parseFloat(p.amount_paid).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </>
        )}

        {session.settlements.length > 0 && (
          <>
            <div className="section-header" style={{ marginTop: 20 }}><div className="section-title">Settlements</div></div>
            {session.settlements.map(s => (
              <div key={s.id} className="card" style={{ padding: '14px 18px' }}>
                <div className="row-between">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{s.from_name} → {s.to_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Rs. {parseFloat(s.amount).toLocaleString()}</div>
                  </div>
                  {s.settled ? (
                    <span className="badge badge-settled">Paid</span>
                  ) : (
                    <button className="btn btn-sm"
                      style={{ background: 'var(--success-light)', color: 'var(--success)', border: '1px solid var(--success)', borderRadius: 8 }}
                      onClick={() => markSettled(s.id)}>
                      Mark paid
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {session.status === 'upcoming' && (
          <div style={{ marginTop: 24 }}>
            <button className="btn btn-primary"
              onClick={() => { setShowPayModal(true); setPayments([{ payerId: session.players[0]?.user_id || '', amountPaid: '' }]); }}>
              💰 Log Payments After Game
            </button>
          </div>
        )}

        {session.status === 'played' && (
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn btn-primary" onClick={sendWhatsApp}>
              📲 Share Summary on WhatsApp
            </button>
            <button className="btn btn-secondary"
              onClick={() => { setShowPayModal(true); setPayments(session.payments.map(p => ({ payerId: p.payer_id, amountPaid: p.amount_paid }))); }}>
              ✏️ Edit Payments
            </button>
          </div>
        )}

        {session.status === 'settled' && (
          <div style={{ marginTop: 24 }}>
            <button className="btn btn-secondary" onClick={sendWhatsApp}>
              📲 Resend Summary on WhatsApp
            </button>
          </div>
        )}
      </div>

      {showPayModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPayModal(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: 20, marginBottom: 20 }}>Log Payments</div>
            {error && <div className="error-msg">{error}</div>}
            {payments.map((p, i) => (
              <div key={i} className="amount-row">
                <select className="form-input" value={p.payerId} onChange={e => updatePayment(i, 'payerId', e.target.value)}>
                  <option value="">Who paid?</option>
                  {session.players.map(pl => <option key={pl.user_id} value={pl.user_id}>{pl.name}</option>)}
                </select>
                <input type="number" className="form-input" placeholder="Amount (Rs)" value={p.amountPaid}
                  onChange={e => updatePayment(i, 'amountPaid', e.target.value)} style={{ width: 130 }} />
                {payments.length > 1 && (
                  <button className="btn btn-ghost" onClick={() => removePaymentRow(i)} style={{ color: 'var(--danger)', padding: '8px' }}>✕</button>
                )}
              </div>
            ))}
            <button className="btn btn-ghost" onClick={addPaymentRow} style={{ color: 'var(--accent2)', marginBottom: 20, padding: '8px 0' }}>
              + Add another payer
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowPayModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={submitPayments} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Payments'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
